import App from './App';
import PropTypes from 'prop-types';
import React, {useCallback} from 'react';
import request, {setAuthToken} from '@apps-common/js-client/api/request';
import {AuthProvider} from '@apps-common/auth-react';
import {CssBaseline, GlobalStyles, ThemeProvider} from '@mui/material';
import {DocumentHandler} from '@apps-common/auth-utils';
import {identify, trackPage} from '@account/analytics';
import {theme} from '@account/styles';
import {useHistory} from 'react-router-dom';
import {useLDClient} from 'launchdarkly-react-client-sdk';

const globalStyles = (
  <GlobalStyles
    styles={{
      'html, body, #root': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
    }}
  />
);

const Account = ({authClient}) => {
  const history = useHistory();
  const ldClient = useLDClient();

  const navigate = useCallback(
    rel => {
      if (rel.includes('./?')) {
        // We need to ensure that the URL includes both query parameters and the hash,
        // use an absolute url with which to navigate after authentication
        const url = new URL(rel, window.location.href);
        window.location.href = url.href;
      } else {
        // We need to strip off the ./# at the beginning of the path for it
        // to work with history.push
        history.push(rel.replace(/^\.\/#/, ''));
      }
    },
    [history]
  );

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    async state => {
      setAuthToken(state.user?.accessToken || '');

      if (!state.pending && !state.error && state.user) {
        const {
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        } = state.user.claims;

        let uid_hash = '';

        // this endpoint only exists with prod auth
        if (import.meta.env.VITE_API_TYPE === 'live') {
          const chameleonIdentity = await request
            .get('https://api.planet.com/explorer-utils/chameleonIdentity')
            .then(res => res.data)
            .catch(() => {
              //do nothing
            });

          uid_hash = chameleonIdentity?.uid_hash ?? '';
        }

        identify({
          user_id,
          email,
          organization_id,
          program_id,
          role_level,
          uid_hash,
        });

        trackPage();

        if (user_id && organization_id) {
          const cgEnv = import.meta.env.VITE_CG_ENV ?? 'staging';

          ldClient.identify({
            kind: 'multi', // See https://launchdarkly.github.io/js-client-sdk/interfaces/LDMultiKindContext.html
            'pda-admin-env': {
              key: `environment-${cgEnv}`,
              cg_env: cgEnv,
            },
            user: {
              key: `user-${user_id}`,
              email,
              role_level,
              user_id,
              _meta: {
                privateAttributes: ['email'], // See https://docs.launchdarkly.com/sdk/features/private-attributes
              },
            },
            organization: {
              key: `organization-${organization_id}`,
              organization_id,
              organization_name,
              program_id,
              _meta: {
                privateAttributes: ['organization_name'], // See https://docs.launchdarkly.com/sdk/features/private-attributes
              },
            },
          });
        }
      }
    },
    [ldClient]
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      private
      onChange={onAuthChange}
    >
      <ThemeProvider theme={theme}>
        {globalStyles}

        <CssBaseline />

        <DocumentHandler>
          <App />
        </DocumentHandler>
      </ThemeProvider>
    </AuthProvider>
  );
};

Account.propTypes = {
  authClient: PropTypes.object,
};

export default Account;
